import { useState, useCallback } from 'react';
import { Plus, X, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface ExclusionReasonConfigProps {
  reasons: string[];
  onChange: (reasons: string[]) => void;
}

const SUGGESTED_REASONS = [
  'Wrong population',
  'Wrong study design',
  'Not primary research',
  'Full text not available',
  'Not in English',
  'Duplicate',
  'Wrong intervention',
  'Wrong outcome',
  'Conference abstract only',
  'Wrong comparator',
  'Insufficient data',
  'Retracted',
];

export function ExclusionReasonConfig({ reasons, onChange }: ExclusionReasonConfigProps) {
  const [newReason, setNewReason] = useState('');

  const addReason = useCallback(
    (reason: string) => {
      const trimmed = reason.trim();
      if (trimmed && !reasons.includes(trimmed)) {
        onChange([...reasons, trimmed]);
      }
      setNewReason('');
    },
    [reasons, onChange],
  );

  const removeReason = useCallback(
    (reason: string) => {
      onChange(reasons.filter((r) => r !== reason));
    },
    [reasons, onChange],
  );

  const suggestionsToShow = SUGGESTED_REASONS.filter((s) => !reasons.includes(s));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Tag className="h-4 w-4 text-indigo-500" />
          <CardTitle className="text-base">Exclusion Reasons</CardTitle>
          <Badge variant="outline" className="text-xs">{reasons.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current reasons */}
        <div className="flex flex-wrap gap-2">
          {reasons.map((reason) => (
            <Badge
              key={reason}
              variant="secondary"
              className="text-xs pr-1 gap-1"
            >
              {reason}
              <button
                type="button"
                onClick={() => removeReason(reason)}
                className="ml-1 rounded-full hover:bg-slate-300 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {reasons.length === 0 && (
            <p className="text-xs text-slate-400 italic">No exclusion reasons configured</p>
          )}
        </div>

        {/* Add new reason */}
        <div className="flex gap-2">
          <Input
            placeholder="Add custom reason..."
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addReason(newReason);
              }
            }}
            className="h-8 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            onClick={() => addReason(newReason)}
            disabled={!newReason.trim()}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>

        {/* Suggestions */}
        {suggestionsToShow.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 mb-2">Suggestions:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestionsToShow.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => addReason(suggestion)}
                  className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                >
                  <Plus className="h-3 w-3 mr-0.5" />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
